import { useAuth } from "@laborelec/lbe-react/auth";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

interface UserInfo {
  /** User ID (sub claim from token) */
  userId: string;
  /** Display name from /userinfo */
  name: string;
  /** Email from /userinfo */
  email: string;
}

const UserContext = createContext<UserInfo>({
  userId: "",
  name: "",
  email: "",
});

/**
 * Derives the OIDC issuer base URL from VITE_OAUTH_URL (which points to /authorize).
 * E.g. "https://api-int.mylaborelec.com/oauth2/1.0/authorize" → "https://api-int.mylaborelec.com/oauth2/1.0"
 */
function getIssuerBase(): string {
  const authUrl = import.meta.env.VITE_OAUTH_URL ?? "";
  return authUrl.replace(/\/authorize$/, "");
}

function getSubFromToken(token: string): string {
  try {
    const payload = token.split(".")[1];
    const claims = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
    );
    return (claims.sub as string) ?? "";
  } catch {
    return "";
  }
}

export function UserProvider({ children }: { children: React.ReactNode }) {
  const { token } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const userId = useMemo(() => (token ? getSubFromToken(token) : ""), [token]);

  useEffect(() => {
    if (!token) {
      setName("");
      setEmail("");
      return;
    }

    let cancelled = false;
    const issuer = getIssuerBase();

    fetch(`${issuer}/userinfo`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`userinfo ${res.status}`);
        return res.json();
      })
      .then((info: { name?: string; email?: string }) => {
        if (!cancelled) {
          setName(info.name ?? "");
          setEmail(info.email ?? "");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setName("");
          setEmail("");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const value = useMemo<UserInfo>(
    () => ({ userId, name, email }),
    [userId, name, email],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

/** Access the current user's id, name, and email. Must be used inside UserProvider. */
export function useCurrentUser(): UserInfo {
  return useContext(UserContext);
}
